import React, { useRef, useEffect, useState } from 'react';

interface RangeSliderProps {
  min: number;
  max: number;
  step?: number;
  minValue: number;
  maxValue: number;
  onChange: (min: number, max: number) => void;
  disabled?: boolean;
  label?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  step = 1,
  minValue,
  maxValue,
  onChange,
  disabled = false,
  label,
}) => {
  const [localMin, setLocalMin] = useState(minValue);
  const [localMax, setLocalMax] = useState(maxValue);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalMin(minValue);
    setLocalMax(maxValue);
  }, [minValue, maxValue]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    const newMin = Math.min(value, localMax - step);
    setLocalMin(newMin);
    onChange(newMin, localMax);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    const newMax = Math.max(value, localMin + step);
    setLocalMax(newMax);
    onChange(localMin, newMax);
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (Number.isNaN(value)) return;
    const clampedValue = Math.max(min, Math.min(value, localMax - step));
    setLocalMin(clampedValue);
    onChange(clampedValue, localMax);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (Number.isNaN(value)) return;
    const clampedValue = Math.min(max, Math.max(value, localMin + step));
    setLocalMax(clampedValue);
    onChange(localMin, clampedValue);
  };

  const minPercent = ((localMin - min) / (max - min)) * 100;
  const maxPercent = ((localMax - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        {label && (
          <label className="text-sm font-semibold text-gray-900">{label}</label>
        )}
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={Number.isNaN(localMin) ? '' : localMin}
            onChange={handleMinInputChange}
            disabled={disabled}
            className="w-20 min-w-[4.5rem] rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:bg-gray-100 disabled:text-gray-400"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={Number.isNaN(localMax) ? '' : localMax}
            onChange={handleMaxInputChange}
            disabled={disabled}
            className="w-20 min-w-[4.5rem] rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:bg-gray-100 disabled:text-gray-400"
          />
        </div>
      </div>

      <div ref={sliderRef} className="relative h-4 flex items-center">
        <div className="absolute w-full h-1 bg-gray-200 rounded" />
        <div
          className="absolute h-1 bg-primary rounded"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localMin}
          onChange={handleMinChange}
          disabled={disabled}
          className="absolute w-full h-1 appearance-none bg-transparent pointer-events-none
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer 
            [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
            [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full 
            [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto
            [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white
            disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localMax}
          onChange={handleMaxChange}
          disabled={disabled}
          className="absolute w-full h-1 appearance-none bg-transparent pointer-events-none
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer 
            [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
            [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full 
            [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto
            [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white
            disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};

export default RangeSlider;

